import { useId, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { DndContext, useDndMonitor, useDraggable } from '@dnd-kit/core'
import clsx from 'clsx'
import { CheckIcon, MicIcon, TrashIcon } from 'lucide-react'
import { Button } from 'ui/button'

import { useAudioRecording } from './hooks'

type AudioRecorderProps = {
  onAudioGenerated: (audio: Blob) => void
}

const AudioRecorderWithoutDnd = ({ onAudioGenerated }: AudioRecorderProps) => {
  const { t } = useTranslation()
  const { isRecording, cancelRecording, stopRecording, startRecording } = useAudioRecording(onAudioGenerated)
  // in a standalone mode additional recording is started right after touchend
  const isTouchStarted = useRef(false)

  const id = useId()
  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({ id })
  const style =
    isDragging && isRecording
      ? {
          transform: `translateX(${transform?.x}px)`,
        }
      : {}

  useDndMonitor({
    onDragMove(e) {
      const X_LIMIT = 60
      const isCardHitLeftLimit = e.delta.x <= -X_LIMIT
      if (isCardHitLeftLimit) cancelRecording()

      const isCardHitRightLimit = e.delta.x >= X_LIMIT
      if (isCardHitRightLimit) void stopRecording()
    },
  })

  return (
    <div className={clsx('relative', isRecording && 'scale-110 transition-[transform]')}>
      <div
        className={clsx(
          'absolute left-1/2 top-1/2 box-content flex h-auto w-56 -translate-x-1/2 -translate-y-1/2 items-center justify-between gap-4 rounded-md bg-slate-100 dark:bg-slate-600',
          isRecording || 'hidden',
        )}
      >
        {isRecording && (
          // progress indicator
          <div className="absolute left-0 z-0 flex h-full w-0 animate-[progress_3s_linear] justify-center rounded-md bg-slate-200 dark:bg-slate-700" />
        )}
        <button
          aria-label={t('Cancel recording')}
          className="z-10 flex h-full w-14 items-center justify-center stroke-red-700 p-2"
        >
          <TrashIcon className="stroke-red-700" onClick={cancelRecording} aria-hidden />
        </button>
        <button aria-label={t('Confirm recording')} className="z-10 flex h-full w-14 items-center justify-center p-2">
          <CheckIcon className="stroke-green-600" onClick={stopRecording} aria-hidden />
        </button>
      </div>
      <Button
        ref={setNodeRef}
        aria-label={t('Record audio')}
        className="relative touch-none select-none overflow-hidden"
        onMouseDown={() => {
          if (isTouchStarted.current) return

          void startRecording()
        }}
        onTouchStart={() => {
          isTouchStarted.current = true
          void startRecording()
        }}
        size="iconLg"
        style={style}
        type="button"
        variant="icon"
        {...attributes}
        {...listeners}
      >
        <MicIcon className="z-10" aria-hidden />
      </Button>
    </div>
  )
}

export const AudioRecorder = (props: AudioRecorderProps) => (
  <DndContext>
    <AudioRecorderWithoutDnd {...props} />
  </DndContext>
)
